/**
 * community-consolidation — the communities whose search signal is pooled onto
 * the community page (Search & AEO plan "Broken Top consolidation (template
 * for Tetherow)", 2026-10-22, search-content/broken-top-consolidation-2026-10-22.md).
 *
 * WHY. In Search Console (Sep 19 to Oct 16) 142 URLs took impressions for
 * queries containing "broken top" and none took a click. The community page
 * took 106 at position 23; the lots and townhomes type pages took 58 and 15,
 * both self canonical and indexed, while /communities/tetherow/types/
 * single-family already canonicalized to its community (SITE-184). The plan's
 * rules, applied to every community in CONSOLIDATED_COMMUNITY_SLUGS:
 *
 *   1. One page per community owns "{Community} homes for sale": the community
 *      page.
 *   2. Every type page in the community is a filter: rel=canonical to the
 *      community page, follow kept, out of the sitemap. No 301, since users
 *      still need the filter (lib/seo/place-type-twin.ts and the type route's
 *      generateMetadata read communityTypeConsolidatesOntoCommunity).
 *   3. Site-wide modules use the community name alone as the anchor; the full
 *      phrase stays in contextual body links (the footer's community doors in
 *      lib/site-nav.ts read communityChromeLabel).
 *
 * The subdivision pages the plan names live beside the subdivision route
 * (app/subdivisions/[slug]/_v3/plat-community-consolidation.ts).
 */
import { communityPath } from '@/lib/communities/community-public-pair'

/** Public community slugs (the /communities/{slug} door). */
// One slug per line so each community can be reverted on its own (Matt,
// 2026-10-22 9:02 AM PT: extend to every community with the same pattern).
// Brasada Ranch and Eagle Crest keep standalone type pages; Crosswater joins
// (/communities/crosswater/types/condos had 0 impressions) (Matt, 2026-10-22
// 3:47 PM and 4:20 PM PT).
export const CONSOLIDATED_COMMUNITY_SLUGS: readonly string[] = [
  'broken-top',
  'tetherow',
  'black-butte-ranch',
  'caldera-springs',
  'crooked-river-ranch',
  'crosswater',
  'inn-of-the-7th-mountain',
  'juniper-preserve',
  'mt-bachelor-village',
  'northwest-crossing',
  'sunriver',
  'three-rivers',
  'widgi-creek',
]

const CONSOLIDATED: ReadonlySet<string> = new Set(CONSOLIDATED_COMMUNITY_SLUGS)

export function isConsolidatedCommunity(publicSlug: string | null | undefined): boolean {
  return CONSOLIDATED.has((publicSlug ?? '').trim().toLowerCase())
}

/**
 * Consolidated community type pages whose canonical goes to the city type page
 * instead of the community page. Sunriver condos: Google ranks
 * /cities/sunriver/types/condos for "sunriver condos for sale", and the
 * community page answers a broader query (brief-sunriver-condos-2026-10-29,
 * Matt YES 2026-10-30). Keyed `{publicSlug}/{typeSlug}`.
 */
const COMMUNITY_TYPE_CANONICAL_OVERRIDES: Readonly<Record<string, string>> = {
  'sunriver/condos': '/cities/sunriver/types/condos',
}

/**
 * The canonical path an override names for a community type page, or null.
 * `targetThin` is true when the target's own count is verifiably under
 * THIN_PAGE_MIN_ACTIVE (getPlaceTypePageNoIndex on the target). The override
 * then stands down and the type page consolidates onto its community page like
 * every other type in the community. Unknown counts pass false (fail open).
 */
export function communityTypeCanonicalOverride(
  publicSlug: string | null | undefined,
  typeSlug: string | null | undefined,
  targetThin = false,
): string | null {
  if (targetThin) return null
  const key = `${(publicSlug ?? '').trim().toLowerCase()}/${(typeSlug ?? '').trim().toLowerCase()}`
  return COMMUNITY_TYPE_CANONICAL_OVERRIDES[key] ?? null
}

/**
 * Where an override target canonicalizes while it is verifiably thin: the
 * community page the override took the type page from, so a thin city type
 * page does not self-canonicalize under a noindex (review on #1359,
 * 2026-10-30). Null for a page no override names, or one that is not thin.
 */
export function overrideTargetThinCanonical(pagePath: string | null | undefined, thin: boolean): string | null {
  if (!thin) return null
  const path = (pagePath ?? '').trim().toLowerCase().replace(/\/+$/, '')
  for (const [key, target] of Object.entries(COMMUNITY_TYPE_CANONICAL_OVERRIDES)) {
    if (target === path) return communityPath(key.split('/')[0])
  }
  return null
}

/**
 * The visible text of a site-wide door to a community page: the bare name for
 * a consolidated community (rule 3), the exact-match anchor otherwise.
 */
export function communityChromeLabel(hub: { slug: string; label: string; anchor: string }): string {
  return isConsolidatedCommunity(hub.slug) ? hub.label : hub.anchor
}
